import { accumulativeContext } from '@react-three/drei';
import {
    forwardRef,
    useCallback,
    useContext,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
} from 'react';
import type {
    DirectionalLight,
    Group,
} from 'three';
import { Vector3 } from 'three';
import { SHADOW_LIGHT_SAMPLE_SEED } from './config';

export interface DeterministicRandomizedLightHandle {
    reset: () => void
}

interface Props {
    ambient: number
    amount: number
    bias: number
    intensity: number
    mapSize: number
    position: [number, number, number]
    radius: number
    rotationY: number
    size: number
}

const UINT32_RANGE = 0x100000000;

/**
 * Mulberry32 provides a small, repeatable random stream. The stream is only
 * used for light placement; it does not alter browser-global entropy.
 */
const createRandom = (seed:number) => {
    let state = seed;
    return () => {
        state = (state + 0x6d2b79f5) | 0;
        let value = state;
        value = Math.imul(value ^ (value >>> 15), value | 1);
        value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
        return ((value ^ (value >>> 14)) >>> 0) / UINT32_RANGE;
    };
};

/**
 * Drei's RandomizedLight uses Math.random(), so separate render workers bake
 * different textures for identical scenes. This equivalent light rig derives
 * every update from one fixed seed and the sample index instead.
 */
export default forwardRef<
    DeterministicRandomizedLightHandle,
    Props
>(function DeterministicRandomizedLight({
    ambient,
    amount,
    bias,
    intensity,
    mapSize,
    position,
    radius,
    rotationY,
    size,
}, ref) {
    const [positionX, positionY, positionZ] = position;
    const lights = useRef<Group>(null);
    const sampleIndex = useRef(0);
    const parent = useContext(accumulativeContext);
    const lightDistance = useMemo(
        () => new Vector3(positionX, positionY, positionZ).length(),
        [positionX, positionY, positionZ],
    );

    const reset = useCallback(() => {
        sampleIndex.current = 0;
    }, []);
    const update = useCallback(() => {
        const group = lights.current;
        if(!group) return;

        // Make each update independently reproducible. Resetting the bake only
        // has to rewind the sample index; prior request order is irrelevant.
        const updateSeed = (
            SHADOW_LIGHT_SAMPLE_SEED
            ^ Math.imul(sampleIndex.current + 1, 0x9e3779b1)
        ) >>> 0;
        sampleIndex.current++;
        const random = createRandom(updateSeed);
        const randomSpread = () => radius * (0.5 - random());
        const rotationCosine = Math.cos(rotationY);
        const rotationSine = Math.sin(rotationY);

        group.children.forEach(child => {
            const light = child as DirectionalLight;
            if(random() > ambient) {
                light.position.set(
                    positionX + randomSpread(),
                    positionY + randomSpread(),
                    positionZ + randomSpread(),
                );
            } else {
                const latitude = Math.acos(2 * random() - 1) - Math.PI / 2;
                const longitude = 2 * Math.PI * random();
                light.position.set(
                    Math.cos(latitude) * Math.cos(longitude) * lightDistance,
                    Math.abs(
                        Math.cos(latitude)
                        * Math.sin(longitude)
                        * lightDistance
                    ),
                    Math.sin(latitude) * lightDistance,
                );
            }

            const unrotatedX = light.position.x;
            const unrotatedZ = light.position.z;
            light.position.x = unrotatedX * rotationCosine
                + unrotatedZ * rotationSine;
            light.position.z = -unrotatedX * rotationSine
                + unrotatedZ * rotationCosine;
        });
    }, [
        ambient,
        lightDistance,
        positionX,
        positionY,
        positionZ,
        radius,
        rotationY,
    ]);
    const api = useMemo(() => ({update}), [update]);

    useImperativeHandle(ref, () => ({reset}), [reset]);
    useLayoutEffect(reset, [reset]);
    useLayoutEffect(() => {
        const group = lights.current;
        if(!group) return;

        parent.lights.set(group.uuid, api);
        return () => {
            parent.lights.delete(group.uuid);
        };
    }, [api, parent]);

    return <group ref={lights}>
        {Array.from({length: amount}, (_, index) => (
            <directionalLight
                key={index}
                castShadow
                intensity={intensity / amount}
                shadow-bias={bias}
                shadow-mapSize={[mapSize, mapSize]}
            >
                <orthographicCamera
                    attach="shadow-camera"
                    args={[-size, size, size, -size, 0.5, 500]}
                />
            </directionalLight>
        ))}
    </group>;
});
